import React from 'react';
import { Typography, TypographyProps, styled } from '@mui/material';

import { IItemReadResourceV10 } from 'components/basket/model/Basket';
import { isDefined } from 'lib/typeInference';
import { addOpacity } from 'lib/helpers';

const StyledUL = styled('ul')(({ theme }) => ({
    listStyle: 'none',
    paddingLeft: theme.spacing(1),
    '& li:before': { content: '"- "', color: addOpacity(theme.palette.text.primary, 0.6) },
    '& li': {
        textIndent: '-5px'
    }
}));

const StyledTypography = styled(Typography)(({ theme }) => ({
    color: addOpacity(theme.palette.text.primary, 0.6),
    display: 'inline'
}));

interface IRecursiveModifierProductListProps {
    modifierProducts: IItemReadResourceV10[];
    orderItemQuantity?: number;
    typographyVariant?: TypographyProps['variant'];
}

export const RecursiveModifierProductList = ({
    modifierProducts,
    orderItemQuantity = 1,
    typographyVariant = 'caption'
}: IRecursiveModifierProductListProps) => {
    const productName = React.useCallback(
        (modifierProduct: IItemReadResourceV10) => {
            const computedQuantity = modifierProduct.quantity / orderItemQuantity;
            const productName =
                computedQuantity > 1
                    ? `${computedQuantity}x ${modifierProduct.productName}`
                    : modifierProduct.productName;
            return productName;
        },
        [orderItemQuantity]
    );
    const collapsedChildrenString = React.useCallback(
        (children: IItemReadResourceV10[]) => {
            const formatter = new Intl.ListFormat('en-GB', {
                style: 'long',
                type: 'conjunction'
            });
            return formatter.format(children.map(child => productName(child)));
        },
        [productName]
    );
    const hasNestedChildren = React.useCallback(
        (modifierProduct: IItemReadResourceV10) =>
            modifierProduct.children &&
            modifierProduct.children.some(child => child.children && child.children.length > 0),
        []
    );
    return (
        <StyledUL>
            {modifierProducts.map(modifierProduct => (
                <React.Fragment key={modifierProduct.id}>
                    <li key={modifierProduct.id}>
                        <StyledTypography variant={typographyVariant} whiteSpace="pre-wrap">
                            {productName(modifierProduct)}
                            {isDefined(modifierProduct.children) &&
                                modifierProduct.children.length > 0 &&
                                !hasNestedChildren(modifierProduct) &&
                                ` : ${collapsedChildrenString(modifierProduct.children || [])}`}
                        </StyledTypography>
                    </li>

                    {hasNestedChildren(modifierProduct) && (
                        <RecursiveModifierProductList
                            modifierProducts={modifierProduct.children || []}
                            orderItemQuantity={orderItemQuantity}
                            typographyVariant={typographyVariant}
                        />
                    )}
                </React.Fragment>
            ))}
        </StyledUL>
    );
};
